import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, Paperclip, X } from 'lucide-react'
import { Modal } from '../common/Modal'
import { Button } from '../common/Button'
import { WORK_TYPE_OPTIONS } from '../../lib/workTypes'
import { isReadyForNextStageStatus, requiresStatusDetailsPopup } from '../../lib/readyForNextStage'
import { getAllowedAttachmentMessage, isAllowedAttachmentFile } from '../../lib/attachments'
import type { TransitionPopupPayload, ProofType } from '../../lib/statusTransitionSubmission'
import type { Issue, IssueStatus, Label, TimeLogStatus, User, WorkType } from '../../types'

type TimeLogWorkTypeValue = WorkType | ''
type TimeLogStatusValue = TimeLogStatus | ''

type ReadyForNextStageModalProps = {
  isOpen: boolean
  issue: Pick<Issue, 'identifier' | 'title' | 'labels' | 'statusId'> | null
  statuses: Pick<IssueStatus, 'id' | 'name' | 'color'>[]
  labels: Label[]
  user?: User | null
  initialNextStatusId?: string | null
  lockNextStatus?: boolean
  isSubmitting?: boolean
  onClose: () => void
  onSubmit: (payload: TransitionPopupPayload) => void
}

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const toTimeLogHours = (hours: string, minutes: string) =>
  (parseInt(hours || '0', 10) || 0) + ((parseInt(minutes || '0', 10) || 0) / 60)

export default function ReadyForNextStageModal({
  isOpen,
  issue,
  statuses,
  labels,
  user,
  initialNextStatusId = null,
  lockNextStatus = false,
  isSubmitting = false,
  onClose,
  onSubmit,
}: ReadyForNextStageModalProps) {
  const [nextStatusId, setNextStatusId] = useState('')
  const [selectedLabelId, setSelectedLabelId] = useState('')
  const [logDate, setLogDate] = useState(todayDateInput())
  const [logWorkType, setLogWorkType] = useState<TimeLogWorkTypeValue>('')
  const [logStatus, setLogStatus] = useState<TimeLogStatusValue>('')
  const [logHoursValue, setLogHoursValue] = useState('0')
  const [logMinutesValue, setLogMinutesValue] = useState('0')
  const [logProgressPercent, setLogProgressPercent] = useState('')
  const [logProgressNotes, setLogProgressNotes] = useState('')
  const [logCompletedDetails, setLogCompletedDetails] = useState('')
  const [proofType, setProofType] = useState<ProofType | ''>('')
  const [proofLink, setProofLink] = useState('')
  const [proofReason, setProofReason] = useState('')
  const [proofFiles, setProofFiles] = useState<File[]>([])
  const proofFileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    setNextStatusId(initialNextStatusId || '')
    setSelectedLabelId('')
    setLogDate(todayDateInput())
    setLogWorkType(user?.role === 'TESTER' ? 'TESTING' : '')
    setLogStatus('')
    setLogHoursValue('0')
    setLogMinutesValue('0')
    setLogProgressPercent('')
    setLogProgressNotes('')
    setLogCompletedDetails('')
    setProofType('')
    setProofLink('')
    setProofReason('')
    setProofFiles([])
  }, [initialNextStatusId, isOpen])

  const currentStatus = statuses.find((status) => status.id === issue?.statusId) || null
  const nextStatus = statuses.find((status) => status.id === nextStatusId) || null
  const isMovingIntoReady = isReadyForNextStageStatus(nextStatus?.name)
  const requiresTransitionDetails = requiresStatusDetailsPopup({
    currentStatusName: currentStatus?.name,
    nextStatusName: nextStatus?.name,
    userRole: user?.role,
  })
  const totalHours = toTimeLogHours(logHoursValue, logMinutesValue)
  const progressPercentNumber = parseInt(logProgressPercent, 10)
  const currentLabelNames = useMemo(
    () => issue?.labels.map(({ label }) => label.name).join(', ') || '',
    [issue]
  )
  const selectedLabel = labels.find((label) => label.id === selectedLabelId) || null
  const isScreenshotProofValid = proofType === 'SCREENSHOT' && proofFiles.length > 0
  const isRecordingProofValid = proofType === 'SCREEN_RECORDING_LINK' && !!proofLink.trim()
  const isNoProofValid = proofType === 'NO_PROOF_REQUIRED' && !!proofReason.trim()
  const isProofValid = !requiresTransitionDetails || !!proofType && (isScreenshotProofValid || isRecordingProofValid || isNoProofValid)

  const isTimeLogValid =
    !!logWorkType &&
    !!logStatus &&
    totalHours > 0 &&
    totalHours <= 24 &&
    (
      logStatus === 'PROGRESSING'
        ? Number.isInteger(progressPercentNumber) &&
          progressPercentNumber >= 1 &&
          progressPercentNumber <= 99 &&
          !!logProgressNotes.trim()
        : !!logCompletedDetails.trim()
    )

  const isSubmitDisabled =
    !nextStatusId ||
    nextStatusId === issue?.statusId ||
    (requiresTransitionDetails && ((isMovingIntoReady && !selectedLabelId) || !isTimeLogValid || !isProofValid))

  const addProofFiles = (files: FileList | File[] | null) => {
    if (!files?.length) return
    const nextFiles = Array.from(files)
    const validImages = nextFiles.filter((file) => file.type.startsWith('image/'))
    const rejected = nextFiles.filter((file) => !file.type.startsWith('image/') || !isAllowedAttachmentFile(file))
    if (rejected.length > 0) {
      window.alert(`Only image files are allowed here. ${getAllowedAttachmentMessage()}`)
    }
    setProofFiles((current) => [...current, ...validImages])
    if (proofFileInputRef.current) proofFileInputRef.current.value = ''
  }

  const handleSubmit = () => {
    if (!issue || isSubmitDisabled) return

    if (!requiresTransitionDetails) {
      onSubmit({ targetStatusId: nextStatusId })
      return
    }

    onSubmit({
      targetStatusId: nextStatusId,
      ...(isMovingIntoReady ? { labelIds: [selectedLabelId] } : {}),
      timeLog: {
        workType: logWorkType as WorkType,
        status: logStatus as TimeLogStatus,
        hours: totalHours,
        progressPercent: logStatus === 'PROGRESSING' ? progressPercentNumber : undefined,
        progressNotes: logStatus === 'PROGRESSING' ? logProgressNotes.trim() : undefined,
        completedDetails: logStatus === 'COMPLETED' ? logCompletedDetails.trim() : undefined,
        loggedDate: `${logDate}T00:00:00.000Z`,
      },
      proof: proofType === 'SCREENSHOT'
        ? { type: proofType, screenshotFiles: proofFiles }
        : proofType === 'SCREEN_RECORDING_LINK'
          ? { type: proofType, screenRecordingLink: proofLink.trim() }
          : { type: 'NO_PROOF_REQUIRED', noProofReason: proofReason.trim() },
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Work Status" width={620}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={statusFlowStyle}>
          <div style={statusBadgeStyle(currentStatus?.color || '#94a3b8')}>
            {currentStatus?.name || 'Current status'}
          </div>
          <ArrowRight size={16} style={{ color: '#64748b', flexShrink: 0 }} />
          {lockNextStatus ? (
            <div style={lockedNextStatusStyle(nextStatus?.color || '#94a3b8')}>
              {nextStatus?.name || 'Please select'}
            </div>
          ) : (
            <select value={nextStatusId} onChange={(event) => setNextStatusId(event.target.value)} style={statusSelectStyle}>
              <option value="">Please select</option>
              {statuses
                .filter((status) => status.id !== issue?.statusId)
                .map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
            </select>
          )}
        </div>

        {requiresTransitionDetails && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {isMovingIntoReady ? (
                <div>
                  <label style={sectionLabelStyle}>Label *</label>
                  <select value={selectedLabelId} onChange={(event) => setSelectedLabelId(event.target.value)} style={formInputStyle}>
                    <option value="">Please select</option>
                    {labels.map((label) => (
                      <option key={label.id} value={label.id}>
                        {label.name}
                      </option>
                    ))}
                  </select>
                  {currentLabelNames && (
                    <div style={helpTextStyle}>
                      Current label: {currentLabelNames}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label style={sectionLabelStyle}>Status Note</label>
                  <div style={{ ...formInputStyle, background: '#f8fafc', color: '#64748b' }}>
                    Time details are required for this move so work is tracked properly.
                  </div>
                </div>
              )}
              <div>
                <label style={sectionLabelStyle}>Work Type *</label>
                <select value={logWorkType} onChange={(event) => setLogWorkType(event.target.value as TimeLogWorkTypeValue)} style={formInputStyle}>
                  <option value="">Please select</option>
                  {WORK_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={sectionLabelStyle}>Duration</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input type="number" min="0" max="24" step="1" value={logHoursValue} onChange={(event) => setLogHoursValue(event.target.value)} style={formInputStyle} placeholder="0" />
                  <input type="number" min="0" max="59" step="1" value={logMinutesValue} onChange={(event) => setLogMinutesValue(event.target.value)} style={formInputStyle} placeholder="0" />
                </div>
              </div>
              <div>
                <label style={sectionLabelStyle}>Time Status *</label>
                <select value={logStatus} onChange={(event) => setLogStatus(event.target.value as TimeLogStatusValue)} style={formInputStyle}>
                  <option value="">Please select</option>
                  <option value="PROGRESSING">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={sectionLabelStyle}>Employee Name</label>
                <input value={user?.fullName || ''} disabled style={{ ...formInputStyle, background: '#f8fafc' }} />
              </div>
              <div>
                <label style={sectionLabelStyle}>Date</label>
                <input type="date" value={logDate} onChange={(event) => setLogDate(event.target.value)} style={formInputStyle} />
              </div>
            </div>

            {logStatus === 'PROGRESSING' ? (
              <>
                <div>
                  <label style={sectionLabelStyle}>Progress % *</label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    step="1"
                    value={logProgressPercent}
                    onChange={(event) => setLogProgressPercent(event.target.value)}
                    placeholder="Please select"
                    style={formInputStyle}
                  />
                </div>
                <div>
                  <label style={sectionLabelStyle}>Progress Notes *</label>
                  <textarea
                    value={logProgressNotes}
                    onChange={(event) => setLogProgressNotes(event.target.value)}
                    rows={5}
                    placeholder="Provide a detailed summary of the work completed during this session..."
                    style={{ ...formInputStyle, resize: 'vertical', minHeight: 120 }}
                  />
                </div>
              </>
            ) : logStatus === 'COMPLETED' ? (
              <div>
                <label style={sectionLabelStyle}>Completed Details *</label>
                <textarea
                  value={logCompletedDetails}
                  onChange={(event) => setLogCompletedDetails(event.target.value)}
                  rows={5}
                  placeholder="Provide a detailed summary of the work completed during this session..."
                  style={{ ...formInputStyle, resize: 'vertical', minHeight: 120 }}
                />
              </div>
            ) : null}

            {isMovingIntoReady && selectedLabel && (
              <div style={helpTextStyle}>
                Selected label: {selectedLabel.name}
              </div>
            )}

            <div>
              <div style={sectionLabelRowStyle}>
                <label style={{ ...sectionLabelStyle, marginBottom: 0 }}>Evidence *</label>
                <span style={inlineHelpTextStyle}>Helps QA understand quickly. Screen recording preferred.</span>
              </div>
              <select
                value={proofType}
                onChange={(event) => {
                  setProofType(event.target.value as ProofType | '')
                  setProofLink('')
                  setProofReason('')
                  setProofFiles([])
                }}
                style={formInputStyle}
              >
                <option value="">Please select</option>
                <option value="SCREENSHOT">Screenshot</option>
                <option value="SCREEN_RECORDING_LINK">Screen recording link</option>
                <option value="NO_PROOF_REQUIRED">No proof required</option>
              </select>
            </div>

            {proofType === 'SCREENSHOT' && (
              <div>
                <label style={sectionLabelStyle}>Attach Screenshot *</label>
                <input
                  ref={proofFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => addProofFiles(event.target.files)}
                  style={{ display: 'none' }}
                />
                <button type="button" onClick={() => proofFileInputRef.current?.click()} style={proofUploadButtonStyle}>
                  <Paperclip size={14} />
                  Attach screenshot
                </button>
                {proofFiles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                    {proofFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} style={proofFileRowStyle}>
                        <span style={{ fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setProofFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))}
                          style={proofFileRemoveStyle}
                          title="Remove screenshot"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {proofType === 'SCREEN_RECORDING_LINK' && (
              <div>
                <label style={sectionLabelStyle}>Screen Recording Link *</label>
                <input
                  value={proofLink}
                  onChange={(event) => setProofLink(event.target.value)}
                  placeholder="Paste the screen recording link"
                  style={formInputStyle}
                />
              </div>
            )}

            {proofType === 'NO_PROOF_REQUIRED' && (
              <div>
                <label style={sectionLabelStyle}>Reason *</label>
                <textarea
                  value={proofReason}
                  onChange={(event) => setProofReason(event.target.value)}
                  rows={4}
                  placeholder="Explain why screenshot or recording is not required for this task..."
                  style={{ ...formInputStyle, resize: 'vertical', minHeight: 96 }}
                />
              </div>
            )}

          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitDisabled} loading={isSubmitting} onClick={handleSubmit}>
            {requiresTransitionDetails ? 'Save and Move' : 'Change Status'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const statusFlowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  gap: 10,
  alignItems: 'center',
}

const statusBadgeStyle = (color: string): React.CSSProperties => ({
  minHeight: 44,
  borderRadius: 10,
  border: `1px solid ${color}44`,
  background: `${color}14`,
  color: '#1f2937',
  display: 'flex',
  alignItems: 'center',
  padding: '0 14px',
  fontSize: 13,
  fontWeight: 600,
})

const lockedNextStatusStyle = (color: string): React.CSSProperties => ({
  ...statusBadgeStyle(color),
  justifyContent: 'space-between',
})

const statusSelectStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 44,
  borderRadius: 10,
  border: '1px solid #dbe3ef',
  padding: '0 12px',
  fontSize: 13,
  background: '#ffffff',
  outline: 'none',
}

const sectionLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#334155',
  marginBottom: 6,
}

const sectionLabelRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 6,
}

const formInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid #dbe3ef',
  borderRadius: 10,
  outline: 'none',
  boxSizing: 'border-box',
}

const helpTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#64748b',
  marginTop: 6,
}

const inlineHelpTextStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  textAlign: 'right',
}

const proofUploadButtonStyle: React.CSSProperties = {
  height: 38,
  borderRadius: 10,
  border: '1px solid #dbe3ef',
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 14px',
  fontSize: 13,
  fontWeight: 600,
}

const proofFileRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '8px 10px',
  borderRadius: 10,
  border: '1px solid #dbe3ef',
  background: '#ffffff',
}

const proofFileRemoveStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}
